import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useT } from '../../i18n';
import lbDialog from '../UI/Modals/lbDialog';
import { Info, DollarSign, Lock, Coins, Percent } from 'lucide-react';
import { useCynths, loadMarket, loadAccount } from '../../context/useCynths';
import { cdpAddress } from '../../.config.js';
import OpenPosition from './OpenPosition';
import Positions from './Positions';
import StabilityPools from './StabilityPools';
import Liquidations from './Liquidations';
import SynthMarket from './SynthMarket';
import { CATEGORIES, categoryOf, synthName, getTokenIcon, num, formatUsd, formatAmount, formatPct, formatRatio, ratioBps } from './cdpHelpers';

const REFRESH_MS = 30000;
const TABS = ['positions', 'pools', 'liquidations'];

const StatTile = ({ label, value, Icon }) => (
  <div className="lb-tile lb-hoverable p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="lb-label">{label}</p>
        <p className="notranslate lb-num mt-1 font-display text-2xl text-white">{value}</p>
      </div>
      <Icon className="h-7 w-7 text-brand-periwinkle" />
    </div>
  </div>
);

/** What a synth tracks: ciBOLD weights, basket components or its own feed */
const Composition = ({ synth, market }) => {
  const t = useT();
  if (synth.ticker === 'ciBOLD' && market.bold) {
    return (
      <div>
        <h4 className="mb-1 text-sm font-medium text-white">{t('cynths.allocationPct')}</h4>
        <p className="notranslate">
          <span className="font-bold text-orange-500">BTC: {market.bold.btcWeight}%</span> |{' '}
          <span className="font-bold text-yellow-500">Gold: {market.bold.goldWeight}%</span>
        </p>
      </div>
    );
  }
  if (!synth.basket) return null;
  return (
    <div>
      <h4 className="mb-1 text-sm font-medium text-white">{t('cynths.basketPerUnit')}</h4>
      <p className="notranslate text-sm text-white/70">
        {synth.basket.components.map((c, i) => (
          <span key={c} className="mr-3 inline-block">
            <span className="font-bold text-white">{formatAmount(num(synth.basket.amounts[i]), 5)}</span> {c.replace(/^ci/, '')}
          </span>
        ))}
        {!synth.basket.constantUsd.isZero() && (
          <span className="inline-block">
            + <span className="font-bold text-white">{formatUsd(num(synth.basket.constantUsd))}</span>
          </span>
        )}
      </p>
    </div>
  );
};

const MintBurn = () => {
  const t = useT();
  const cynths = useCynths();
  const { address } = cynths;
  const [market, setMarket] = useState(null);
  const [account, setAccount] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState('positions');
  const [category, setCategory] = useState(null);
  const [selected, setSelected] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(async () => {
    if (!cdpAddress) return;
    try {
      const m = await loadMarket();
      setMarket(m);
      setAccount(address ? await loadAccount(address, m) : null);
      setLoadError(null);
    } catch (err) {
      console.error('Error fetching CDP data:', err);
      setLoadError(err.message);
    }
  }, [address]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  // every write refreshes the data; failures inside the tx dialog were already shown to the user
  const actions = useMemo(() => {
    const wrapped = {};
    for (const [name, fn] of Object.entries(cynths)) {
      if (typeof fn !== 'function') continue;
      wrapped[name] = async (...args) => {
        try {
          await fn(...args);
        } catch (err) {
          console.error(`${name} failed:`, err);
          if (!err.handled && !/User rejected|denied/i.test(err.message || '')) {
            lbDialog.fire(t('tx.error'), err.shortMessage || err.message, 'error');
          }
        } finally {
          await refresh();
          setRefreshKey((k) => k + 1);
        }
      };
    }
    return wrapped;
  }, [cynths, refresh, t]);

  const synthsByCategory = useMemo(() => {
    const groups = {};
    for (const s of market?.synths || []) (groups[categoryOf(s.ticker)] ||= []).push(s);
    return groups;
  }, [market]);
  const categories = CATEGORIES.filter((c) => synthsByCategory[c]);
  const activeCategory = categories.includes(category) ? category : categories[0];
  const synth = market?.synths.find((s) => s.ticker === selected) || null;

  const stats = useMemo(() => {
    if (!market) return null;
    const collateralUsd = market.collaterals.reduce(
      (sum, c) => sum + (c.price ? num(c.locked, c.decimals) * num(c.price) : 0),
      0
    );
    const debtUsd = market.synths.reduce((sum, s) => sum + (s.price ? num(s.totalDebt) * num(s.price) : 0), 0);
    return { collateralUsd, debtUsd, ratio: ratioBps(collateralUsd, debtUsd) };
  }, [market]);

  const cranniBalance = account ? account.balances[market.cranni.toLowerCase()] : null;
  const feeBps = cranniBalance && cranniBalance.gte(market.cranniPremium) ? 0 : market?.mintFeeBps ?? 0;

  const addToWallet = async () => {
    if (!synth) return;
    try {
      await window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: { address: synth.token, symbol: synth.ticker, decimals: 18, image: getTokenIcon(synth.ticker) || undefined },
        },
      });
      lbDialog.fire(t('tx.success'), t('dialog.addedToWallet', { token: synth.ticker }), 'success');
    } catch (error) {
      console.error('Failed to add token to Wallet:', error);
      lbDialog.fire(t('tx.error'), t('dialog.addToWalletFailed'), 'error');
    }
  };

  const header = (
    <div className="text-center">
      <p className="lb-eyebrow">{t('cynths.syntheticAssets')}</p>
      <h1 className="lb-heading mt-3 text-3xl sm:text-4xl">
        <span className="lb-gradient-text">{t('cynths.title')}</span>
      </h1>
      <p className="mx-auto mb-8 mt-3 max-w-2xl text-sm text-white/55 sm:text-base">{t('cynths.blurb')}</p>
    </div>
  );

  if (!cdpAddress || (!market && loadError)) {
    return (
      <div className="min-h-screen space-y-6 md:p-2">
        {header}
        <div className="lb-card p-8 text-center text-white/60">
          <Info className="mx-auto mb-4 h-12 w-12" />
          <p>{cdpAddress ? t('cynths.loadFailed') : t('cynths.notDeployed')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen space-y-6 md:p-2">
      <div className="mb-8">
        {header}
        <div className="lb-card grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 sm:p-6 lg:grid-cols-4">
          <StatTile label={t('cynths.tvl')} value={stats ? formatUsd(stats.collateralUsd) : '…'} Icon={DollarSign} />
          <StatTile label={t('cynths.totalDebt')} value={stats ? formatUsd(stats.debtUsd) : '…'} Icon={Coins} />
          <StatTile label={t('cynths.systemRatio')} value={stats ? (stats.debtUsd > 0 ? formatRatio(stats.ratio) : 'N/A') : '…'} Icon={Lock} />
          <StatTile label={t('cynths.mintFee')} value={market ? formatPct(feeBps) : '…'} Icon={Percent} />
        </div>
      </div>

      <div className="flex justify-center">
        <div className="lb-seg">
          {TABS.map((key) => (
            <button key={key} className="lb-seg-item" data-active={tab === key} onClick={() => setTab(key)}>
              {t(`cynths.tab.${key}`)}
            </button>
          ))}
        </div>
      </div>

      {!market ? (
        <div className="lb-card p-8 text-center text-white/50">{t('common.loading')}</div>
      ) : tab === 'positions' ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 md:gap-6">
            <div className="space-y-6">
              <div className="lb-card overflow-hidden">
                <div className="border-b border-white/5 px-6 py-5">
                  <h2 className="lb-heading text-lg">{t('cynths.selectToken')}</h2>
                </div>
                <div className="grid grid-cols-2 gap-2 p-6 sm:grid-cols-3">
                  {categories.map((type) => (
                    <button
                      key={type}
                      className={`rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300 ${
                        activeCategory === type
                          ? 'lb-fill-brand text-white'
                          : 'border border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                      onClick={() => setCategory(type)}
                    >
                      {t(`cynths.category.${type}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="lb-card overflow-hidden">
                <div className="border-b border-white/5 px-6 py-5">
                  <h2 className="lb-heading text-lg">{t('cynths.availableTokens')}</h2>
                </div>
                <div className="grid grid-cols-2 gap-4 p-6">
                  {(synthsByCategory[activeCategory] || []).map((s) => (
                    <div
                      key={s.ticker}
                      onClick={() => setSelected(s.ticker)}
                      className={`cursor-pointer rounded-lg p-4 transition-all duration-300 ${
                        selected === s.ticker
                          ? 'border border-brand-cyan/45 bg-brand-cyan/10'
                          : 'border border-white/10 bg-white/[0.03] hover:border-white/25'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        {getTokenIcon(s.ticker) ? (
                          <img src={getTokenIcon(s.ticker)} alt={s.ticker} className="w-25 h-25 rounded-full" />
                        ) : (
                          <div className="mb-3 mt-3 flex h-14 w-14 items-center justify-center rounded-full lb-fill-brand">
                            <span className="font-bold text-white">{s.ticker.slice(2, 4)}</span>
                          </div>
                        )}
                        <span className="notranslate text-sm font-medium text-white">{s.ticker}</span>
                        <span className="notranslate text-[11px] text-white/45">{synthName(s.ticker)}</span>
                        <span className="notranslate mt-1 text-xs text-white/45">{formatUsd(s.price ? num(s.price) : null)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {synth ? (
              <div className="col-span-2 mt-8 space-y-6 md:mt-0">
                <div className="lb-card overflow-hidden">
                  <div className="flex items-center justify-between border-b border-white/5 px-6 py-5">
                    <h2 className="lb-heading notranslate text-lg">{synth.ticker}</h2>
                    <button onClick={addToWallet} className="lb-btn lb-btn-gold lb-btn-sm">
                      {t('cynths.addToWallet')}
                    </button>
                  </div>
                  <div className="space-y-6 p-6">
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                      <div className="lb-tile-soft p-4">
                        <p className="lb-label">{t('cynths.price')}</p>
                        <p className="notranslate text-xl font-bold text-white">{formatUsd(synth.price ? num(synth.price) : null)}</p>
                      </div>
                      <div className="lb-tile-soft p-4">
                        <p className="lb-label">{t('cynths.minted')}</p>
                        <p className="notranslate text-xl font-bold text-white">{formatAmount(num(synth.totalDebt), 4)}</p>
                        <p className="notranslate text-xs text-white/45">
                          {formatUsd(synth.price ? num(synth.totalDebt) * num(synth.price) : null)}
                        </p>
                      </div>
                      <div className="lb-tile-soft p-4">
                        <p className="lb-label">{t('cynths.debtCeiling')}</p>
                        <p className="notranslate text-xl font-bold text-white">
                          {synth.debtCeiling.isZero() ? '∞' : formatAmount(num(synth.debtCeiling), 2)}
                        </p>
                      </div>
                    </div>

                    <div>
                      <h3 className="lb-heading mb-3 text-lg">{t('cynths.market')}</h3>
                      <SynthMarket synth={synth} />
                    </div>

                    <p className="text-white/70">{t(`cynths.synth.${synth.ticker}`)}</p>
                    <Composition synth={synth} market={market} />

                    <div>
                      <h3 className="lb-heading mb-4 text-lg">{t('cynths.openPosition')}</h3>
                      <OpenPosition market={market} account={account} synth={synth} feeBps={feeBps} onOpen={actions.openPosition} />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="lb-card col-span-2 mt-8 flex min-h-[400px] items-center justify-center overflow-hidden md:mt-0">
                <div className="text-center text-white/50">
                  <Info className="mx-auto mb-4 h-12 w-12" />
                  <p>{t('cynths.selectPrompt')}</p>
                </div>
              </div>
            )}
          </div>

          <div className="lb-card overflow-hidden">
            <div className="border-b border-white/5 px-6 py-5">
              <h2 className="lb-heading text-lg">{t('cynths.myPositions')}</h2>
            </div>
            <div className="p-6">
              <Positions market={market} account={account} actions={actions} />
            </div>
          </div>
        </>
      ) : tab === 'pools' ? (
        <StabilityPools market={market} account={account} actions={actions} />
      ) : (
        <Liquidations market={market} account={account} address={address} actions={actions} refreshKey={refreshKey} onRefresh={refresh} />
      )}
    </div>
  );
};

export default MintBurn;
